import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, PenSquare, MousePointer, Home, Calendar, DollarSign, X } from 'lucide-react';
import SignaturePad from './SignaturePad';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SignLeasePage = ({ token, onClose, onSuccess }) => {
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState(null);
  const [initials, setInitials] = useState({});
  const [signaturePadOpen, setSignaturePadOpen] = useState(false);
  const [signed, setSigned] = useState(false);

  // Sections nécessitant des initiales
  const signatureSections = [
    { id: 'terms', label: 'Terms & Conditions', required: true },
    { id: 'rent', label: 'Rental Agreement', required: true },
    { id: 'deposit', label: 'Security Deposit', required: true },
    { id: 'hangar_rules', label: 'Hangar Rules', required: true },
  ];

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API_BASE}/leases/verify-token/${token}`);
      setLease(res.data);
    } catch (err) {
      setError('Invalid or expired signature link');
    } finally {
      setLoading(false);
    }
  };

  const getIpAddress = async () => {
    try {
      const res = await axios.get('https://api.ipify.org?format=json');
      return res.data.ip;
    } catch {
      return 'unknown';
    }
  };

  const handleInitialsChange = (sectionId, value) => {
    setInitials(prev => ({
      ...prev,
      [sectionId]: {
        initials: value.toUpperCase(),
        timestamp: new Date().toISOString()
      }
    }));
  };

  const handleSign = async () => {
    // Vérifier que toutes les sections obligatoires ont des initiales
    const missingSections = signatureSections
      .filter(s => s.required && !initials[s.id]?.initials)
      .map(s => s.label);
    
    if (missingSections.length > 0) {
      alert(`Please add your initials for: ${missingSections.join(', ')}`);
      return;
    }
    
    if (!signature) {
      alert('Please provide your signature');
      return;
    }
    
    setSigning(true);
    try {
      const ip = await getIpAddress();
      
      await axios.post(`${API_BASE}/leases/sign-with-token`, {
        token: token,
        ip_address: ip,
        initials: Object.entries(initials).map(([section, data]) => ({
          section,
          initials: data.initials,
          timestamp: data.timestamp
        }))
      });
      
      setSigned(true);
      if (onSuccess) onSuccess();
      
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      alert('Error signing lease: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading lease document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-xl p-6 max-w-md mx-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 rounded-lg text-white">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-xl p-6 max-w-md mx-4 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-white font-bold text-lg mb-2">Lease Signed Successfully!</h3>
          <p className="text-gray-400 mb-4">
            Thank you for signing lease {lease?.lease_number}. A confirmation email has been sent.
          </p>
          <button onClick={onClose} className="px-4 py-2 bg-green-600 rounded-lg text-white">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Sign Lease Agreement</h2>
            <p className="text-gray-400 text-sm">{lease?.lease_number} - Hangar {lease?.hangar_number}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Document Content */}
        <div className="p-6 border-b border-gray-700">
          <div className="bg-gray-900 rounded-lg p-6 max-h-[400px] overflow-y-auto">
            <div className="whitespace-pre-wrap font-mono text-sm text-gray-300">
              {lease?.documents || "No lease terms available."}
            </div>
          </div>
        </div>

        {/* Initials Section */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MousePointer size={18} className="text-yellow-400" />
            Initial Each Section
          </h3>
          <div className="space-y-4">
            {signatureSections.map((section) => (
              <div key={section.id} className="flex items-center justify-between flex-wrap gap-3 p-3 bg-gray-700/30 rounded-lg">
                <span className="text-gray-300 text-sm">{section.label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength="3"
                    placeholder="ABC"
                    value={initials[section.id]?.initials || ''}
                    onChange={(e) => handleInitialsChange(section.id, e.target.value)}
                    className="w-20 bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-white text-center font-mono uppercase text-sm"
                  />
                  {initials[section.id]?.initials && (
                    <CheckCircle size={16} className="text-green-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Signature Section */}
        <div className="p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <PenSquare size={18} className="text-blue-400" />
            Electronic Signature
          </h3>
          
          {!signaturePadOpen ? (
            <button
              onClick={() => setSignaturePadOpen(true)}
              className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-500 hover:text-blue-400 transition"
            >
              <PenSquare size={24} className="mx-auto mb-2" />
              Click to sign
            </button>
          ) : (
            <>
              <SignaturePad onSave={setSignature} />
              <button
                onClick={() => setSignaturePadOpen(false)}
                className="mt-3 text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
            </>
          )}
          
          {signature && (
            <div className="mt-4 p-3 bg-green-500/20 rounded-lg flex items-center gap-2">
              <CheckCircle size={18} className="text-green-400" />
              <span className="text-green-400 text-sm">Signature captured</span>
            </div>
          )}

          {/* Legal Disclaimer */}
          <div className="mt-6 p-4 bg-gray-700/30 rounded-lg text-xs text-gray-400">
            <p className="font-semibold text-gray-300 mb-2">By signing above, you agree:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>You have read and understand all terms of this lease</li>
              <li>You are authorized to sign on behalf of the tenant</li>
              <li>This electronic signature is legally binding</li>
              <li>Your IP address will be recorded for verification</li>
            </ul>
          </div>

          {/* Sign Button */}
          <button
            onClick={handleSign}
            disabled={signing || !signature}
            className="mt-6 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            {signing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Sign Lease Agreement
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignLeasePage;